/**
 * Une activité finie, rouverte pour être relue : ce qu'en sait l'écran, en machine d'états pure.
 *
 * Le moteur de l'ancien `ActivityFinishedScreen` (et de la boîte « Mon rendu » qu'il ouvre,
 * `ActivityDetailsModal`), sans écran :
 *  - la copie se relit à l'ouverture (`getActivities` avec ses journaux), puis toutes les 5 s tant
 *    qu'elle attend sa note ou que Sam la corrige (`finishedActivityPolls`) ;
 *  - la correction est marquée lue, une fois (`read-review`) ;
 *  - l'avis sur la correction se relit à l'ouverture du rendu, et se donne d'un geste ;
 *  - « Refaire l'activité » redonne la même activité à l'élève, qui la commence aussitôt.
 *
 * L'état porte ce qui est en vol (la relecture, l'avis, la nouvelle activité) et l'échec à dire. Ce
 * que l'activité porte (sa copie, sa correction, son appréciation), c'est le store qui le tient :
 * la relecture le range dans l'activité courante, comme avant.
 */

/** Ce que la machine lit d'une activité finie (`ActivityResource`). */
export type FinishedActivity = {
    id?: number | null
    status?: string | null
    finished_at?: string | null
    is_review_read?: boolean | null
    correction_status?: string | null
    correction_generating?: boolean | null
    correction_image?: string | null
} & Record<string, unknown>

/** Où en est la correction de la copie : le libellé du bouton de l'ancien écran (`getButtonLabel`). */
export type FinishedCorrection = "generating" | "ready" | "none"

export function finishedCorrection(activity: FinishedActivity | null | undefined): FinishedCorrection {
    if (activity?.correction_generating) return "generating"
    if (activity?.correction_status === "completed" && activity?.correction_image) return "ready"
    return "none"
}

/** La copie se relit tant qu'elle attend sa note, ou que Sam la corrige (`useCorrectionPolling` de l'ancien écran). */
export function finishedActivityPolls(activity: FinishedActivity | null | undefined): boolean {
    return activity?.status === "pending_review" || !!activity?.correction_generating
}

/** La correction n'est pas encore marquée lue. */
export function needsReviewRead(activity: FinishedActivity | null | undefined): boolean {
    return activity?.id != null && !activity.is_review_read
}

export type CorrectionFeedbackValue = "positive" | "negative"

/** L'échec à dire : le serveur a refusé la nouvelle activité, ou la correction marquée lue. */
export type FinishedActivityNotice = { kind: "redo_failed" | "read_review_failed"; message: string | null }

export type FinishedActivityState = {
    activityId: number | null
    /** La copie se relit. */
    loading: boolean
    /** L'avis affiché : celui que l'élève vient de donner, pendant son envoi. */
    feedback: CorrectionFeedbackValue | null
    /** L'avis que le serveur tient : celui que l'avis affiché retrouve si son envoi échoue. */
    feedbackSent: CorrectionFeedbackValue | null
    feedbackSubmitting: boolean
    /** L'envoi de l'avis a échoué : il est revenu, et l'écran le dit (le message du serveur, s'il en a un). */
    feedbackFailure: { message: string | null } | null
    /** « Refaire l'activité » : la nouvelle activité est demandée. */
    redoing: boolean
    notice: FinishedActivityNotice | null
}

export type FinishedActivityEvent =
    | { type: "activity"; activityId: number | null }
    | { type: "loadStarted" }
    | { type: "loadEnded" }
    | { type: "feedbackRead"; value: CorrectionFeedbackValue | null }
    | { type: "feedbackGiven"; value: CorrectionFeedbackValue }
    | { type: "feedbackSettled" }
    | { type: "feedbackFailed"; message: string | null }
    | { type: "redoStarted" }
    | { type: "redoFailed"; message: string | null }
    | { type: "redoDone" }
    | { type: "reviewReadFailed"; message: string | null }
    | { type: "noticeDismissed" }

export function initialFinishedActivityState(activityId: number | null): FinishedActivityState {
    return {
        activityId,
        loading: false,
        feedback: null,
        feedbackSent: null,
        feedbackSubmitting: false,
        feedbackFailure: null,
        redoing: false,
        notice: null,
    }
}

export function finishedActivityReducer(
    state: FinishedActivityState,
    event: FinishedActivityEvent
): FinishedActivityState {
    switch (event.type) {
        case "activity":
            // Une autre activité repart de rien : son avis, son échec ne sont pas ceux d'avant.
            return event.activityId === state.activityId ? state : initialFinishedActivityState(event.activityId)
        case "loadStarted":
            return state.loading ? state : { ...state, loading: true }
        case "loadEnded":
            return state.loading ? { ...state, loading: false } : state
        case "feedbackRead":
            // Un avis donné pendant la relecture l'emporte sur celui qu'elle rapporte.
            if (state.feedbackSubmitting || event.value == null) return state
            return { ...state, feedback: event.value, feedbackSent: event.value }
        case "feedbackGiven":
            if (state.feedbackSubmitting) return state
            return { ...state, feedback: event.value, feedbackSubmitting: true, feedbackFailure: null }
        case "feedbackSettled":
            return state.feedbackSubmitting
                ? { ...state, feedbackSubmitting: false, feedbackSent: state.feedback }
                : state
        case "feedbackFailed":
            // L'avis qui n'est pas parti ne reste pas affiché comme donné : celui d'avant revient.
            if (!state.feedbackSubmitting) return state
            return {
                ...state,
                feedback: state.feedbackSent,
                feedbackSubmitting: false,
                feedbackFailure: { message: event.message },
            }
        case "redoStarted":
            if (state.redoing) return state
            return { ...state, redoing: true, notice: null }
        case "redoFailed":
            return { ...state, redoing: false, notice: { kind: "redo_failed", message: event.message } }
        case "redoDone":
            return state.redoing ? { ...state, redoing: false } : state
        case "reviewReadFailed":
            return { ...state, notice: { kind: "read_review_failed", message: event.message } }
        case "noticeDismissed":
            return state.notice == null ? state : { ...state, notice: null }
        default:
            return state
    }
}

/** Le nom d'un état, pour le fil d'Ariane : ce qui est en vol. */
export function finishedActivityPhase(state: FinishedActivityState): string {
    if (state.redoing) return "redoing"
    if (state.notice != null) return `notice:${state.notice.kind}`
    if (state.loading) return "loading"
    return "idle"
}
